import React, { useEffect, useState } from 'react';
import { Box, Snackbar, Alert, Button } from '@mui/material';
import { checkForNewVersion, updateStoredVersion, getBuildVersion } from '../utils/version';

/**
 * VersionChecker Component
 * Checks for new app versions and prompts user to reload
 */
const VersionChecker = () => {
  const [showReloadPrompt, setShowReloadPrompt] = useState(false);
  const [newVersionDetected, setNewVersionDetected] = useState(false);

  useEffect(() => {
    // Check if we just reloaded (to prevent showing notification again)
    const reloadInProgress = sessionStorage.getItem('version_reload_in_progress');
    if (reloadInProgress) {
      console.log('✅ Version reload completed, clearing flag');
      sessionStorage.removeItem('version_reload_in_progress');
      updateStoredVersion();
      // Clear reload counter
      localStorage.removeItem('version_reload_count');
      return;
    }

    // Check on mount
    const storedVersion = localStorage.getItem('app_version');
    const currentVersion = getBuildVersion();
    const storedTimestamp = localStorage.getItem('app_build_timestamp');
    const currentTimestamp = import.meta.env.VITE_BUILD_TIME;
    
    console.log('🔍 Version check:', { 
      stored: storedVersion, 
      current: currentVersion,
      storedTimestamp,
      currentTimestamp,
      match: storedVersion === currentVersion && storedTimestamp === currentTimestamp
    });
    
    const hasNewVersion = checkForNewVersion();
    if (hasNewVersion) {
      // Check reload counter to prevent infinite loop
      const reloadCount = parseInt(localStorage.getItem('version_reload_count') || '0');
      
      if (reloadCount >= 3) {
        // If we've reloaded 3+ times and still seeing this, force update and stop showing
        console.warn('⚠️ Version notification shown 3+ times, forcing version update to stop loop');
        updateStoredVersion();
        localStorage.removeItem('version_reload_count');
        return;
      }
      
      console.log('🔔 New version detected, showing notification (attempt', reloadCount + 1, '/3)');
      setNewVersionDetected(true);
      setShowReloadPrompt(true);
    } else {
      console.log('✅ No new version, updating stored version');
      updateStoredVersion();
      // Clear reload counter if no new version
      localStorage.removeItem('version_reload_count');
    }

    // Check periodically (every 5 minutes)
    const interval = setInterval(() => {
      // In production, you could fetch a version.json file from the server
      // For now, we just check localStorage
      const hasUpdate = checkForNewVersion();
      if (hasUpdate && !sessionStorage.getItem('version_reload_in_progress')) {
        console.log('🔔 New version detected (periodic check), showing notification');
        setNewVersionDetected(true);
        setShowReloadPrompt(true);
      }
    }, 5 * 60 * 1000); // 5 minutes

    return () => clearInterval(interval);
  }, []);

  const handleReload = async () => {
    console.log('🔄 Forcing hard reload...');
    
    // Increment reload counter
    const reloadCount = parseInt(localStorage.getItem('version_reload_count') || '0');
    localStorage.setItem('version_reload_count', (reloadCount + 1).toString());
    
    // Set a flag to prevent showing the notification again after reload
    sessionStorage.setItem('version_reload_in_progress', 'true');
    
    // Clear all caches
    if ('caches' in window) {
      try {
        const names = await caches.keys();
        await Promise.all(names.map(name => caches.delete(name)));
        console.log('✅ Cleared', names.length, 'caches');
      } catch (err) {
        console.warn('⚠️ Could not clear caches:', err);
      }
    }
    
    // Force hard reload by replacing current location
    // This bypasses all caches more reliably than reload(true)
    window.location.replace(window.location.href.split('?')[0] + '?_v=' + Date.now());
  };

  const handleDismiss = () => {
    setShowReloadPrompt(false);
    updateStoredVersion();
    // Clear reload counter when dismissed
    localStorage.removeItem('version_reload_count');
  };

  if (!newVersionDetected) {
    return null;
  }

  return (
    <Snackbar
      open={showReloadPrompt}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      sx={{ mt: 8 }}
    >
      <Alert
        severity="info"
        sx={{
          bgcolor: 'rgba(20, 241, 149, 0.1)',
          border: '1px solid rgba(20, 241, 149, 0.3)',
          '& .MuiAlert-icon': { color: '#14F195' }
        }}
        action={
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              size="small"
              onClick={handleDismiss}
              sx={{ color: '#fff' }}
            >
              Later
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={handleReload}
              sx={{
                bgcolor: '#14F195',
                color: '#000',
                '&:hover': { bgcolor: '#00cc00' }
              }}
            >
              Reload Now
            </Button>
          </Box>
        }
      >
        <strong>New Version Available!</strong>
        <br />
        A new version of GraveForge is available. Reload to get the latest features and fixes.
        <br />
        <small style={{ opacity: 0.7, fontSize: '0.75rem' }}>
          (If this keeps appearing, click "Later" to dismiss)
        </small>
      </Alert>
    </Snackbar>
  );
};

export default VersionChecker;
